import { useCallback, useEffect, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

import { GoogleAddressPredictionFields } from "globalConstants";
import { mapGeocodeAddress } from "mappers";

const REQUEST_PARAMS = {
	language: "en",
	region: "us",
	types: ["address"],
	componentRestrictions: { country: ["us", "ca"] },
};

const DETAILS_FIELDS = ["formatted_address", "address_components", "geometry"];

type UseGoogleGeocodeService = {
	htmlDivElement: HTMLDivElement | null | undefined;
};

const useGoogleGeocodeService = ({
	htmlDivElement, // Div placeholder to pass to the PlacesService
}: UseGoogleGeocodeService) => {
	const places = useMapsLibrary("places");

	const [sessionToken, setSessionToken] =
		useState<google.maps.places.AutocompleteSessionToken>();
	const [autocompleteService, setAutocompleteService] =
		useState<google.maps.places.AutocompleteService | null>(null);
	const [placesService, setPlacesService] =
		useState<google.maps.places.PlacesService | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!places || !htmlDivElement) return;

		setAutocompleteService(new places.AutocompleteService());
		setPlacesService(new places.PlacesService(htmlDivElement));
		setSessionToken(new places.AutocompleteSessionToken());

		return () => setAutocompleteService(null);
	}, [htmlDivElement, places]);

	const fetchPredictions = useCallback(
		async (
			inputValue: string,
			onSuccess: (predictionsData: { [key: string]: any }[]) => void,
			onError: () => void,
		) => {
			if (!autocompleteService || !inputValue) {
				onError();
				return;
			}

			const request = {
				...REQUEST_PARAMS,
				input: inputValue,
				sessionToken,
			};
			setIsLoading(true);

			try {
				const response = await autocompleteService.getPlacePredictions(
					request,
				);
				onSuccess(
					mapGeocodeAddress(
						response[GoogleAddressPredictionFields.Predictions],
					),
				);
			} catch {
			} finally {
				setIsLoading(false);
			}
		},
		[autocompleteService, sessionToken],
	);

	const getAddressDetails = useCallback(
		(
			placeId: string,
			onSuccess: (addressDetails: { [key: string]: any }) => void,
		) => {
			if (!places || !placeId) return;

			const detailRequestOptions = {
				placeId,
				fields: DETAILS_FIELDS,
				sessionToken,
			};

			const detailsRequestCallback = (
				placeDetails: google.maps.places.PlaceResult | null,
			) => {
				onSuccess(
					placeDetails ? mapGeocodeAddress([placeDetails])[0] : {},
				);
				setSessionToken(new places.AutocompleteSessionToken());
			};

			placesService?.getDetails(
				detailRequestOptions,
				detailsRequestCallback,
			);
		},
		[places, placesService, sessionToken],
	);

	return { fetchPredictions, getAddressDetails, isLoading };
};

export default useGoogleGeocodeService;
